import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CreditCard, DeviceMobile, ArrowRight, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import { formatGHS, formatUSD } from "@/lib/format";
import { createStripeSession, initializePaystack } from "@/lib/api";

const regions = [
  "Greater Accra", "Ashanti", "Western", "Eastern", "Central",
  "Volta", "Northern", "Upper East", "Upper West", "Bono", "Ahafo",
  "Bono East", "Oti", "North East", "Savannah", "Western North",
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotalGHS, subtotalUSD } = useCart();
  const [method, setMethod] = useState("paystack");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    region: "Greater Accra",
    notes: "",
  });

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-32 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-4xl font-black uppercase mb-4">Cart is empty</h1>
        <Link to="/products" className="text-[#FFC107] uppercase tracking-wider font-bold">
          Browse hardware →
        </Link>
      </div>
    );
  }

  const total = method === "paystack" ? subtotalGHS : subtotalUSD;
  const fmt = method === "paystack" ? formatGHS : formatUSD;

  const referralCode = (localStorage.getItem("obrasafe_ref") || "").trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
      customer: form,
      payment_method: method,
      origin_url: window.location.origin,
      referral_code: referralCode || null,
    };
    try {
      if (method === "stripe") {
        const res = await createStripeSession(payload);
        window.location.href = res.url;
      } else {
        const res = await initializePaystack(payload);
        window.location.href = res.authorization_url;
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Payment initialization failed";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="bg-[#0a0a0a] text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <Link to="/cart" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-[#FFC107] mb-6">
          <ArrowLeft size={14} weight="bold" /> Back to cart
        </Link>
        <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tight mb-12">Checkout</h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-10">
            <div>
              <h2 className="font-display text-xl font-black uppercase mb-6">// Delivery Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="ck-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" className="h-12 px-4 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
                <input data-testid="ck-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (e.g. 0244 000 000)" className="h-12 px-4 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
                <input data-testid="ck-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="h-12 px-4 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm sm:col-span-2" />
                <input data-testid="ck-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street address / Landmark" className="h-12 px-4 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm sm:col-span-2" />
                <input data-testid="ck-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City / Town" className="h-12 px-4 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
                <select data-testid="ck-region" required value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="h-12 px-4 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm">
                  {regions.map((r) => <option key={r}>{r}</option>)}
                </select>
                <textarea data-testid="ck-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Delivery notes (optional)" rows={2} className="p-4 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm sm:col-span-2" />
              </div>
            </div>

            <div>
              <h2 className="font-display text-xl font-black uppercase mb-6">// Payment Method</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  data-testid="payment-paystack"
                  onClick={() => setMethod("paystack")}
                  className={`p-5 text-left border transition-colors ${
                    method === "paystack"
                      ? "border-[#FFC107] bg-[#FFC107]/5"
                      : "border-white/15 hover:border-white/40"
                  }`}
                >
                  <DeviceMobile size={28} weight="duotone" className="text-[#FFC107] mb-3" />
                  <h3 className="font-display font-black uppercase mb-1">Paystack</h3>
                  <p className="text-xs text-zinc-400 leading-relaxed">MTN MoMo • Vodafone Cash • AirtelTigo • Card. Local pricing in GHS.</p>
                </button>
                <button
                  type="button"
                  data-testid="payment-stripe"
                  onClick={() => setMethod("stripe")}
                  className={`p-5 text-left border transition-colors ${
                    method === "stripe"
                      ? "border-[#FFC107] bg-[#FFC107]/5"
                      : "border-white/15 hover:border-white/40"
                  }`}
                >
                  <CreditCard size={28} weight="duotone" className="text-[#FFC107] mb-3" />
                  <h3 className="font-display font-black uppercase mb-1">Stripe</h3>
                  <p className="text-xs text-zinc-400 leading-relaxed">International cards (Visa, Mastercard, Amex). Pricing in USD.</p>
                </button>
              </div>
              <div className="mt-4 flex items-start gap-3 p-4 border border-white/10 bg-[#141414]">
                <ShieldCheck size={20} weight="duotone" className="text-[#FFC107] shrink-0 mt-0.5" />
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Your payment is processed securely. We never store card or MoMo PIN data on our servers.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-[#141414] border border-white/10 p-6 sticky top-24">
              <h3 className="font-display text-xl font-black uppercase mb-6">Summary</h3>
              <div className="space-y-3 text-sm mb-5">
                {items.map(({ product, quantity }) => (
                  <div key={product.id} className="flex justify-between gap-3" data-testid={`ck-line-${product.slug}`}>
                    <span className="text-zinc-400 line-clamp-1">{product.name} × {quantity}</span>
                    <span className="font-mono-accent text-white whitespace-nowrap">
                      {fmt((method === "paystack" ? product.price_ghs : product.price_usd) * quantity)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 my-5" />
              <div className="flex justify-between items-end mb-6">
                <span className="text-xs uppercase tracking-widest text-zinc-500">Total</span>
                <span data-testid="ck-total" className="font-display text-3xl font-black text-[#FFC107]">{fmt(total)}</span>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="ck-pay-btn"
                className="w-full h-13 py-3 bg-[#FFC107] hover:bg-[#FFB300] disabled:opacity-50 text-black font-black uppercase tracking-wider text-sm flex items-center justify-center gap-2"
              >
                {submitting ? "Redirecting…" : <>Pay {fmt(total)} <ArrowRight size={18} weight="bold" /></>}
              </button>
              <p className="text-[11px] text-zinc-500 text-center mt-3">By placing this order you accept our terms of service.</p>
              {referralCode && (
                <p data-testid="referral-applied" className="text-[11px] text-[#FFC107] text-center mt-2">
                  Referral code {referralCode} applied
                </p>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
